/**
 * sw-register.ts
 *
 * Service Worker 手動注冊入口。
 * 生產模式使用 vite-plugin-pwa 的 `virtual:pwa-register` 觸發 SW 注冊，
 * 並在有新版本可用時透過 antd notification 提示用戶更新。
 *
 * 使用方式：在 main.tsx 中 import 並調用 registerServiceWorker()。
 */
/**
 * 注冊 Service Worker 並設置更新通知。
 * - onNeedRefresh：有新 SW 等待激活時，顯示通知讓用戶決定是否立即套用。
 * - onOfflineReady：SW 成功預快取所有資源，可完全離線使用時觸發。
 */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV && import.meta.env.VITE_PWA_DEV === '1') {
    void import('./dev-sw-register')
      .then(({ registerDevServiceWorker }) => {
        registerDevServiceWorker();
      })
      .catch((err: unknown) => {
        console.error('[pwa] dev service worker bootstrap failed', err);
      });
    return;
  }

  void import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        onNeedRefresh() {
          // 動態引入 antd notification，避免在 SW 注冊階段就打包整個 antd
          void import('antd')
            .then(({ notification }) => {
              notification.info({
                key: 'pwa-update', // 固定 key，避免多次偵測時彈出重複通知
                message: '系統有更新',
                description: '點擊此通知以套用最新版本（會重新整理頁面）',
                duration: 0,
                onClick: () => {
                  // updateSW(true)：通知 waiting SW SKIP_WAITING，激活後自動 reload 頁面
                  void updateSW(true);
                },
              });
            })
            .catch((err: unknown) => {
              console.error('[pwa] update notification failed', err);
            });
        },
        onOfflineReady() {
          // SW 已就緒，應用程式可完全離線運行
          console.log('[pwa] offline ready');
        },
        /**
         * onRegisteredSW：SW 注冊成功後拿到 registration，主動輪詢檢查新版本。
         * 否則瀏覽器只在「導航 / 約每 24h」才檢查 SW，長時間開著的管理後台分頁
         * 部署後不會自動彈更新通知，用戶仍得手動刷新一次才觸發檢測。
         * 這裡每 60s 檢查一次，並在分頁重新可見 / 網絡恢復時立即檢查，
         * 讓部署後通常 1 分鐘內自動彈出更新提示。
         */
        onRegisteredSW(swUrl, registration) {
          if (!registration) return;
          const UPDATE_CHECK_INTERVAL_MS = 60_000;

          const checkForUpdate = () => {
            // 僅在線檢查，離線時 update() 必然失敗、徒增報錯噪音
            if (!navigator.onLine) return;
            registration.update().catch((err: unknown) => {
              console.error({ err, swUrl }, '[pwa] service worker update check failed');
            });
          };

          // 定時輪詢
          setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
          // 分頁從後台切回前台時立即檢查（用戶部署後通常會切回標籤頁）
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') checkForUpdate();
          });
          // 網絡恢復時立即檢查
          window.addEventListener('online', checkForUpdate);
        },
      });
    })
    .catch((err: unknown) => {
      console.error('[pwa] service worker bootstrap failed', err);
    });
}
